import { UserFriendlyError } from '@affine/error';
import {
  type GetDocRolePermissionsQuery,
  getDocRolePermissionsQuery,
  type GetWorkspaceRolePermissionsQuery,
  getWorkspaceRolePermissionsQuery,
} from '@affine/graphql';
import { Store } from '@toeverything/infra';

import type { WorkspaceServerService } from '../../cloud';
import type { WorkspaceService } from '../../workspace';

export type WorkspacePermissionActions = keyof Omit<
  GetWorkspaceRolePermissionsQuery['workspaceRolePermissions']['permissions'],
  '__typename'
>;

export type DocPermissionActions = keyof Omit<
  GetDocRolePermissionsQuery['workspace']['doc']['permissions'],
  '__typename'
>;

// Terminal "you have no access" responses must not retry; the backoffRetry
// pipeline in GuardService would otherwise loop forever and leave can$ at
// undefined, which pins the UI on a skeleton/spinner. Instead we return a
// fully-populated zero-permissions object so can$ can resolve to `false`
// and the route can render the NoPermission screen.
function allDocPermissionsDenied(): Record<DocPermissionActions, boolean> {
  return {
    Doc_Copy: false,
    Doc_Delete: false,
    Doc_Duplicate: false,
    Doc_Properties_Read: false,
    Doc_Properties_Update: false,
    Doc_Publish: false,
    Doc_Read: false,
    Doc_Restore: false,
    Doc_TransferOwner: false,
    Doc_Trash: false,
    Doc_Update: false,
    Doc_Users_Manage: false,
    Doc_Users_Read: false,
    Doc_Comments_Create: false,
    Doc_Comments_Delete: false,
    Doc_Comments_Read: false,
    Doc_Comments_Resolve: false,
  };
}

function allWorkspacePermissionsDenied(): Record<
  WorkspacePermissionActions,
  boolean
> {
  return {
    Workspace_Administrators_Manage: false,
    Workspace_Blobs_List: false,
    Workspace_Blobs_Read: false,
    Workspace_Blobs_Write: false,
    Workspace_Copilot: false,
    Workspace_CreateDoc: false,
    Workspace_Delete: false,
    Workspace_Organize_Read: false,
    Workspace_Payment_Manage: false,
    Workspace_Properties_Create: false,
    Workspace_Properties_Delete: false,
    Workspace_Properties_Read: false,
    Workspace_Properties_Update: false,
    Workspace_Read: false,
    Workspace_Settings_Read: false,
    Workspace_Settings_Update: false,
    Workspace_Sync: false,
    Workspace_TransferOwner: false,
    Workspace_Users_Manage: false,
    Workspace_Users_Read: false,
  };
}

function isForbidden(err: unknown): boolean {
  // GraphQLService.gql wraps every thrown error in UserFriendlyError before
  // re-throwing, so a raw `instanceof GraphQLError` check never matches here.
  // Detect the terminal 403 via UserFriendlyError, with a duck-type fallback
  // for any code path that slips through without the wrapper.
  if (err instanceof UserFriendlyError) return err.status === 403;
  const maybe = err as { status?: unknown } | null | undefined;
  return typeof maybe?.status === 'number' && maybe.status === 403;
}

export class GuardStore extends Store {
  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService
  ) {
    super();
  }

  async getWorkspacePermissions(): Promise<
    Record<WorkspacePermissionActions, boolean>
  > {
    if (!this.workspaceServerService.server) {
      throw new Error('No server');
    }
    try {
      const data = await this.workspaceServerService.server.gql({
        query: getWorkspaceRolePermissionsQuery,
        variables: {
          id: this.workspaceService.workspace.id,
        },
      });
      return data.workspaceRolePermissions.permissions;
    } catch (err) {
      if (isForbidden(err)) return allWorkspacePermissionsDenied();
      throw err;
    }
  }

  async getDocPermissions(
    docId: string
  ): Promise<Record<DocPermissionActions, boolean>> {
    if (!this.workspaceServerService.server) {
      throw new Error('No server');
    }
    try {
      const data = await this.workspaceServerService.server.gql({
        query: getDocRolePermissionsQuery,
        variables: {
          workspaceId: this.workspaceService.workspace.id,
          docId,
        },
      });
      return data.workspace.doc.permissions;
    } catch (err) {
      if (isForbidden(err)) return allDocPermissionsDenied();
      throw err;
    }
  }
}
